/**
 * Highlight Polygon Index
 * 
 * @Author: Focci
 * @Date: 2024-09-14 13:58:13
 * @Last Modified by: Focci
 * @Last Modified time: 2024-09-14 13:58:13
 */

// import PropTypes from 'prop-types'
import { toSlug } from '@lib/utils'
import { useMap, Source, Layer } from 'react-map-gl'
import { isFunction, map } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMapState } from '@comp/mapbox'
import { getConfig, getFilterConf, setMapboxConf } from './lib'
import HighlightMarker from './HighlightMarker'

export default function HighlightPolygon({
  lock,
  currentIds,
  feature,
  autoMoveToCenter = true,
  markerProps,
  onClick,
  onMousemove,
  onMouseleave,
  onPolygonChange,
}) {
  const { current } = useMap()
  const mp = current.getMap()
  const mps = useMapState()

  const curSuburb = useRef()

  const cfg = useMemo(() => getConfig({ currentIds }), [currentIds])
  
  /**
   * mouse move
   */
  const handleMove = useCallback((e) => {
    curSuburb.current = { features: map(e.features, (it) => ({ properties: it.properties })) }
    if(lock || !mps.suburbShadow.hoverBoundary) return

    isFunction(onMousemove) && onMousemove(e)

    mp.getCanvas().style.cursor = 'pointer'
    mp.setFilter(cfg.layerLine.id, [
      'all', 
      ...getFilterConf('==', e?.features, cfg.filterProp).filters,
      ...map(currentIds, (id) => ['!=', cfg.filterProp, id])
    ])
    mp.setFilter(cfg.layer.id, [
      'all',
      ...getFilterConf('!=', e?.features, cfg.filterProp).filters, 
      ...map(currentIds, (id) => ['!=', cfg.filterProp, id])
    ])
  }, [cfg, mp, currentIds, lock, onMousemove, mps.suburbShadow.hoverBoundary])

  /**
   * reset layer styles
   */
  const resetLayerStyle = useCallback((e) => {
    const { filters, vals } = getFilterConf('!=', e?.features, cfg.filterProp)
    
    mp.setFilter(cfg.layerLine.id, ['==', cfg.filterProp, ''])
    mp.setFilter(cfg.layer.id, ['all', ...filters])

    isFunction(onPolygonChange) && onPolygonChange(vals)
  }, [cfg, mp, onPolygonChange])

  /**
   * polygon click
   */
  const handleClick = useCallback(() => {
    if(lock || !curSuburb.current) return

    const e = curSuburb.current
    if(isFunction(onClick)) {
      const { name, id } = e.features?.[0]?.properties || {}
      const suburb = [{ name, value: id, slug: toSlug(name) }]

      onClick({ suburb }, 'polygon')
    }

    resetLayerStyle(e)
    autoMoveToCenter && setMapboxConf(mp, e)
  }, [autoMoveToCenter, lock, mp, onClick, resetLayerStyle])

  /**
   * 鼠标离开地图
   */
  const handleLeave = useCallback((e) => {
    if(lock || !mps.suburbShadow.hoverBoundary) return

    isFunction(onMouseleave) && onMouseleave(e)
    
    mp.getCanvas().style.cursor = ''
    mp.setFilter(cfg.layerLine.id, ['==', cfg.filterProp, ''])
    mp.setFilter(cfg.layer.id, [
      'all',
      ...map(currentIds, (id) => ['!=', cfg.filterProp, id])
    ])
  }, [cfg, mp, currentIds, lock, onMouseleave, mps.suburbShadow.hoverBoundary])

  useEffect(() => {
    current.on('mousemove', cfg.layerAll.id, handleMove)
    current.on('mouseleave', cfg.layerAll.id, handleLeave)
    current.on('mouseout', handleLeave)

    return () => {
      current.off('mousemove', cfg.layerAll.id, handleMove)
      current.off('mouseleave', cfg.layerAll.id, handleLeave)
      current.off('mouseout', handleLeave)
    }
  }, [current, cfg, mp, handleMove, handleLeave])
  
  return (
    <>
      <Source {...cfg.source} id={cfg.layer.source}>
        <Layer {...cfg.layerAll} beforeId="bridge" />
        {mps.suburbShadow.status === 1 && <Layer {...cfg.layer} beforeId="bridge" />}
        <Layer {...cfg.layerLine} />
      </Source>
      {feature && (
        <HighlightMarker
          data={feature}
          onClick={handleClick}
          {...markerProps}
        />
      )}
    </>
  )
}
